import { useCallback, useState } from "react";

import { downloadBlob } from "@/shared/utils/downloadBlob";

import { PayoutBatchesService } from "../services/payoutBatches.service";
import type { PayoutBatchDetail } from "../types/payoutBatches.types";
import { type BatchError, classifyBatchError } from "../utils/batchErrors";

export type BatchActionKind = "revalidate" | "cancel" | "report" | "file" | "submit" | "reject" | "withdraw";

export interface UsePayoutBatchActionsResult {
  revalidate: () => Promise<BatchError | null>;
  submit: (fingerprint: string) => Promise<BatchError | null>;
  reject: (reason: string) => Promise<BatchError | null>;
  withdraw: () => Promise<BatchError | null>;
  cancel: (reason?: string) => Promise<BatchError | null>;
  downloadReport: () => Promise<BatchError | null>;
  /** Opens the archived original file (a short-lived signed URL) in a new tab. */
  openFile: () => Promise<BatchError | null>;
  pending: Record<BatchActionKind, boolean>;
}

const IDLE: Record<BatchActionKind, boolean> = {
  revalidate: false,
  cancel: false,
  report: false,
  file: false,
  submit: false,
  reject: false,
  withdraw: false,
};

/** The batch actions that need no step-up code. Each resolves with the classified error, or null on success. */
export function usePayoutBatchActions(
  batchId: string,
  applyBatch: (next: PayoutBatchDetail) => void,
): UsePayoutBatchActionsResult {
  const [pending, setPending] = useState<Record<BatchActionKind, boolean>>(IDLE);

  const run = useCallback(
    async (kind: BatchActionKind, action: () => Promise<void>): Promise<BatchError | null> => {
      setPending((prev) => ({ ...prev, [kind]: true }));
      try {
        await action();
        return null;
      } catch (err: unknown) {
        return classifyBatchError(err);
      } finally {
        setPending((prev) => ({ ...prev, [kind]: false }));
      }
    },
    [],
  );

  const revalidate = useCallback(
    () =>
      run("revalidate", async () => {
        const { data } = await PayoutBatchesService.revalidate(batchId);
        applyBatch(data);
      }),
    [applyBatch, batchId, run],
  );

  const submit = useCallback(
    (fingerprint: string) =>
      run("submit", async () => {
        const { data } = await PayoutBatchesService.submit(batchId, fingerprint);
        applyBatch(data);
      }),
    [applyBatch, batchId, run],
  );

  const reject = useCallback(
    (reason: string) =>
      run("reject", async () => {
        const { data } = await PayoutBatchesService.reject(batchId, reason);
        applyBatch(data);
      }),
    [applyBatch, batchId, run],
  );

  const withdraw = useCallback(
    () =>
      run("withdraw", async () => {
        const { data } = await PayoutBatchesService.withdraw(batchId);
        applyBatch(data);
      }),
    [applyBatch, batchId, run],
  );

  const cancel = useCallback(
    (reason?: string) =>
      run("cancel", async () => {
        const { data } = await PayoutBatchesService.cancel(batchId, reason);
        applyBatch(data);
      }),
    [applyBatch, batchId, run],
  );

  const downloadReport = useCallback(
    () =>
      run("report", async () => {
        const file = await PayoutBatchesService.downloadReport(batchId);
        downloadBlob(file.blob, file.filename);
      }),
    [batchId, run],
  );

  const openFile = useCallback(
    () =>
      run("file", async () => {
        const { data } = await PayoutBatchesService.getFileUrl(batchId);
        window.open(data.url, "_blank", "noopener,noreferrer");
      }),
    [batchId, run],
  );

  return { revalidate, submit, reject, withdraw, cancel, downloadReport, openFile, pending };
}
